import type {
  ConsentStyle,
  OnboardingV4Beat,
  OnboardingV4Flow,
  OnboardingV4FlowID,
  Who,
} from 'config/onboardingV4/flows';

export type OnboardingV4Stage = 'conversation' | 'confirm' | 'finished';

/** The age asked again after the under 18 stop, until a tap answers it: still being asked, said in their own words and being read from `from`, or heard from what they said. */
export type AgeAgain =
  | { kind: 'asking' }
  | { kind: 'said'; messageIDs: string[]; from: string }
  | { kind: 'heard' };

/** Where someone is in onboarding v4, saved with the chat session. */
export interface OnboardingV4Progress {
  flowID: OnboardingV4FlowID;
  /** Kept from the session's start, so a flag changed later leaves it where it is. */
  consentStyle: ConsentStyle;
  stage: OnboardingV4Stage;
  isAiConsentGiven?: boolean;
  who?: Who;
  /** Beats finished, by id, so a reordered flow keeps everyone's place. */
  done: string[];
  /** What has been said or asked, by beat or question id, so nothing is said twice. */
  asked: string[];
  /** Questions answered in their own words rather than by a tap. */
  saidInOwnWords: string[];
  /** When what they said started being read, as an ISO time. */
  readingFrom?: string;
  /** The messages being read before the flow goes on. */
  readingIDs: string[];
  /** Whether what they sent has been read, so the reading card stays ticked through a later reading. */
  isSentRead?: boolean;
  /** Where they chose to start. */
  start?: string;
  /** How many times what we heard has been read again after "Not quite". */
  heardRound: number;
  /** The summary last read back, so the next round waits for a newer one. */
  heardSummaryID?: string;
  /** Whether they were asked to tell us again, after nothing could be made out. */
  isRetold?: boolean;
  /** How many times the under 18 stop has been left behind, by changing the age or by an age read since. */
  under18Round?: number;
  /** The age asked again after the stop; said, it is read before the stop is judged again, and an age read from it replaces one tapped before. */
  ageAgain?: AgeAgain;
  /** Their own corrections of what we heard, by the card they corrected. */
  summaryEdits: Record<string, string[]>;
  /** Whether they want help with what we offered. */
  wantsHelp?: string;
  /** What they sent of what we'd need, each read as it lands. */
  gatheredIDs: string[];
  /** The answer picked on each menu, by its message. */
  menuPicks: Record<string, string>;
  /** Replies shown as the pick on their menu rather than as a message. */
  pickedReplyIDs: string[];
}

export const newProgress = (
  flowID: OnboardingV4FlowID,
  consentStyle: ConsentStyle = 'screen',
): OnboardingV4Progress => ({
  flowID,
  consentStyle,
  stage: 'conversation',
  done: [],
  asked: [],
  saidInOwnWords: [],
  readingIDs: [],
  heardRound: 0,
  summaryEdits: {},
  gatheredIDs: [],
  menuPicks: {},
  pickedReplyIDs: [],
});

/** What a session's metadata holds under the v4 key, as saved. */
export const savedProgress = (metadata?: Record<string, unknown>) => {
  const saved = metadata?.onboardingV4;
  return typeof saved === 'object' && saved !== null
    ? (saved as Record<string, unknown>)
    : undefined;
};

/** Saved progress read back, keeping only what has the right shape. */
export const progressFrom = (
  metadata: Record<string, unknown> | undefined,
  flowID: OnboardingV4FlowID,
): OnboardingV4Progress => {
  const strings = (value: unknown) =>
    Array.isArray(value)
      ? value.filter((v): v is string => typeof v === 'string')
      : [];
  const ageAgain = (value: unknown): AgeAgain | undefined => {
    const saved =
      typeof value === 'object' && value !== null
        ? (value as Record<string, unknown>)
        : undefined;
    if (saved?.kind === 'asking') return { kind: 'asking' };
    if (saved?.kind === 'heard') return { kind: 'heard' };
    if (saved?.kind === 'said' && typeof saved.from === 'string') {
      return {
        kind: 'said',
        messageIDs: strings(saved.messageIDs),
        from: saved.from,
      };
    }
    return undefined;
  };
  const p = savedProgress(metadata);
  if (!p) return newProgress(flowID);
  return {
    flowID,
    // Sessions begun before it was saved asked on a screen.
    consentStyle: p.consentStyle === 'chat' ? 'chat' : 'screen',
    stage:
      p.stage === 'confirm' || p.stage === 'finished'
        ? p.stage
        : 'conversation',
    isAiConsentGiven:
      typeof p.isAiConsentGiven === 'boolean' ? p.isAiConsentGiven : undefined,
    who:
      p.who === 'someone-else' || p.who === 'myself' || p.who === 'exploring'
        ? p.who
        : undefined,
    done: strings(p.done),
    asked: strings(p.asked),
    saidInOwnWords: strings(p.saidInOwnWords),
    readingFrom: typeof p.readingFrom === 'string' ? p.readingFrom : undefined,
    readingIDs: strings(p.readingIDs),
    isSentRead: typeof p.isSentRead === 'boolean' ? p.isSentRead : undefined,
    start: typeof p.start === 'string' ? p.start : undefined,
    heardRound: typeof p.heardRound === 'number' ? p.heardRound : 0,
    heardSummaryID:
      typeof p.heardSummaryID === 'string' ? p.heardSummaryID : undefined,
    isRetold: typeof p.isRetold === 'boolean' ? p.isRetold : undefined,
    under18Round:
      typeof p.under18Round === 'number' ? p.under18Round : undefined,
    ageAgain: ageAgain(p.ageAgain),
    summaryEdits: editsFrom(p.summaryEdits),
    wantsHelp: typeof p.wantsHelp === 'string' ? p.wantsHelp : undefined,
    gatheredIDs: strings(p.gatheredIDs),
    menuPicks: picksFrom(p.menuPicks),
    pickedReplyIDs: strings(p.pickedReplyIDs),
  };
};

/** Saved picks read back, keeping only an option id per menu. */
const picksFrom = (value: unknown): Record<string, string> => {
  if (typeof value !== 'object' || value === null) return {};
  return Object.fromEntries(
    Object.entries(value).filter(
      (entry): entry is [string, string] => typeof entry[1] === 'string',
    ),
  );
};

/** Saved corrections read back, keeping only lists of lines. */
const editsFrom = (value: unknown): Record<string, string[]> => {
  if (typeof value !== 'object' || value === null) return {};
  const edits: Record<string, string[]> = {};
  for (const [id, lines] of Object.entries(value)) {
    if (Array.isArray(lines)) {
      edits[id] = lines.filter((l): l is string => typeof l === 'string');
    }
  }
  return edits;
};

/** Just looking has nobody to ask about. */
const isBeatOffered = (
  beat: OnboardingV4Beat,
  progress: OnboardingV4Progress,
) => !(beat.kind === 'questions' && progress.who === 'exploring');

/** The first beat offered and not yet finished, or null once the conversation is over. */
export const currentBeat = (
  flow: OnboardingV4Flow,
  progress: OnboardingV4Progress,
): OnboardingV4Beat | null =>
  flow.beats.find(
    (beat) => isBeatOffered(beat, progress) && !progress.done.includes(beat.id),
  ) ?? null;

/** Each reading back of what we heard is said once, by its round. */
export const heardKey = (round: number) =>
  round === 0 ? 'heard' : `heard:${round}`;

/** Marks a round's correction as asked for, once "Not quite" is tapped. */
export const correctingKey = (round: number) =>
  round === 0 ? 'heard:correcting' : `heard:${round}:correcting`;

/** A change to where they are, applied to the progress as it is saved. */
export type ProgressChange = (
  progress: OnboardingV4Progress,
) => OnboardingV4Progress;

/** What they sent is being read, from `at`, before the flow goes on. */
export const readingStarted =
  (messageIDs: string[], at: string): ProgressChange =>
  (p) => ({ ...p, readingFrom: at, readingIDs: messageIDs });

/** Sent while what came before is still being read, it is read with it, from `at`. */
export const readingJoined =
  (messageIDs: string[], at: string): ProgressChange =>
  (p) => ({
    ...p,
    readingFrom: at,
    readingIDs: [...p.readingIDs, ...messageIDs],
    isSentRead: undefined,
  });

/** What they sent has been read, and stays ticked as read. */
export const sentRead = (): ProgressChange => (p) => ({
  ...p,
  isSentRead: true,
});

/** Their correction is read, and what we heard is read back once more. */
export const heardAgain =
  (messageIDs: string[], at: string): ProgressChange =>
  (p) => ({
    ...readingStarted(messageIDs, at)(p),
    heardRound: p.heardRound + 1,
  });

/** A round of what we heard has been read back, from this summary. */
export const heardRead =
  (round: number, summaryID: string | undefined): ProgressChange =>
  (p) => ({
    ...p,
    asked: [...p.asked, heardKey(round)],
    heardSummaryID: summaryID,
  });

/** Nothing could be made out: the talk is open again, once. */
export const talkReopened = (): ProgressChange => (p) => ({
  ...p,
  done: p.done.filter((id) => id !== 'talk'),
  readingFrom: undefined,
  readingIDs: [],
  isRetold: true,
});

/** What they need help with, asked in this round of what we heard. */
export const helpWithKey = (round: number) => `help-with:${round}`;

/** Whether what they need help with was asked in this round of what we heard. */
export const isHelpWithAskedIn = (
  { asked }: OnboardingV4Progress,
  round: number,
) => asked.includes(helpWithKey(round));

/** Whether what they need help with has been asked, which is only ever once. */
export const isHelpWithAsked = ({ asked }: OnboardingV4Progress) =>
  asked.some((id) => id.startsWith('help-with:'));

/** The under 18 stop, said again after each change of age. */
export const under18Key = ({ under18Round = 0 }: OnboardingV4Progress) =>
  `under-18:${under18Round}`;

/** The age asked again, once for each change. */
export const ageAgainKey = ({ under18Round = 0 }: OnboardingV4Progress) =>
  `age-again:${under18Round}`;

const nextRound = (p: OnboardingV4Progress) => (p.under18Round ?? 0) + 1;

/** "Change the age": it is asked again. */
export const ageChangeAsked = (): ProgressChange => (p) => ({
  ...p,
  under18Round: nextRound(p),
  ageAgain: { kind: 'asking' },
});

/** The stop lifted by an age read since: said afresh should it come back. */
export const under18Lifted = (): ProgressChange => (p) => ({
  ...p,
  under18Round: nextRound(p),
});

/** The age tapped again. */
export const ageTapped = (): ProgressChange => (p) => ({
  ...p,
  ageAgain: undefined,
});

/** The age said again in their own words, read from `at` before the flow goes on; an age read from it replaces what they tapped before. */
export const ageSaid =
  (messageIDs: string[], at: string): ProgressChange =>
  (p) => ({ ...p, ageAgain: { kind: 'said', messageIDs, from: at } });

/** An age heard from what they said again, kept over the tap before it whatever becomes of the fact. */
export const ageHeard = (): ProgressChange => (p) => ({
  ...p,
  ageAgain: { kind: 'heard' },
});

export const answeredInOwnWords =
  (questionID: string): ProgressChange =>
  (p) => ({
    ...p,
    saidInOwnWords: p.saidInOwnWords.includes(questionID)
      ? p.saidInOwnWords
      : [...p.saidInOwnWords, questionID],
  });

export const gathered =
  (messageIDs: string[]): ProgressChange =>
  (p) => ({ ...p, gatheredIDs: [...p.gatheredIDs, ...messageIDs] });

export const summaryEdited =
  (cardID: string, lines: string[]): ProgressChange =>
  (p) => ({ ...p, summaryEdits: { ...p.summaryEdits, [cardID]: lines } });

/** An answer picked on a menu; its reply is shown as the pick. */
export const menuPicked =
  (menuID: string, optionID: string, replyID?: string): ProgressChange =>
  (p) => ({
    ...p,
    menuPicks: { ...p.menuPicks, [menuID]: optionID },
    pickedReplyIDs: replyID ? [...p.pickedReplyIDs, replyID] : p.pickedReplyIDs,
  });

/** Later: what we'd need is left, and so is sending it. */
export const needSkipped = (): ProgressChange => (p) => ({
  ...p,
  done: [...p.done, 'need', 'gather'],
});
